// Cave84 — shared bottle valuation helper.
//
// Any aggregate that represents "value" (region summaries, cellar summaries,
// portfolio/market value totals) must price each bottle using MARKET price
// first, falling back to COST only when market is unavailable. A bottle with
// neither is never silently counted as €0 — it is excluded from the value
// sum and flagged as unpriced instead, so €0 gaps stay visible rather than
// quietly deflating a total.
//
// Cost basis (used for P&L) is a distinct, separately-labeled metric and
// must never be blended into a "value" number without being tagged as such.

export type ValuationSource = 'market' | 'cost';

export interface BottleValuation {
  /** Priced value for this bottle, or null if neither market nor cost is available. */
  value: number | null;
  /** Which price fed `value` — null when unpriced. */
  source: ValuationSource | null;
}

/**
 * Resolve a single bottle's value for aggregate "value" displays.
 * Fallback chain: market price → cost (only if market is missing) → unpriced.
 * A missing value or a value of 0 are both treated as "no price" — neither a
 * €0 market quote nor a €0 cost is a real valuation.
 */
export function valuateBottle(
  marketPrice: number | null | undefined,
  costEur: number | null | undefined,
): BottleValuation {
  if (typeof marketPrice === 'number' && marketPrice > 0) {
    return { value: marketPrice, source: 'market' };
  }
  if (typeof costEur === 'number' && costEur > 0) {
    return { value: costEur, source: 'cost' };
  }
  return { value: null, source: null };
}

export interface ValuationTotals {
  /** Sum of valuateBottle().value across priced bottles only. */
  valueEur: number;
  /** Bottles that contributed to valueEur (market or cost fallback). */
  pricedCount: number;
  /** Bottles with neither market nor cost — excluded from valueEur. */
  unpricedCount: number;
}

export function emptyValuationTotals(): ValuationTotals {
  return { valueEur: 0, pricedCount: 0, unpricedCount: 0 };
}

/** Fold one bottle's valuation into a running ValuationTotals accumulator. */
export function accumulateValuation(
  totals: ValuationTotals,
  marketPrice: number | null | undefined,
  costEur: number | null | undefined,
): ValuationTotals {
  const { value } = valuateBottle(marketPrice, costEur);
  if (value === null) {
    return { ...totals, unpricedCount: totals.unpricedCount + 1 };
  }
  return { ...totals, valueEur: totals.valueEur + value, pricedCount: totals.pricedCount + 1 };
}
